/**
 * The installed app (a PWA window on the desktop or the phone). On the desktop it draws its own
 * title bar (Window Controls Overlay, opted into by the manifest's display_override): the frame
 * strip and the rail's brand row drag the window (`titlebar` in styles/index.css), and the
 * window buttons sit on the theme colour, which follows the frame colour here.
 */
import { useEffect } from 'react';
import { usePrefs } from './prefs.ts';

const INSTALLED_MODES = ['(display-mode: standalone)', '(display-mode: window-controls-overlay)'];

/** True in an installed app window, whether it shows the system title bar or draws its own. */
export function isInstalledApp(): boolean {
  if (typeof window === 'undefined') return false;
  return INSTALLED_MODES.some((q) => window.matchMedia?.(q).matches) || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

/**
 * The window title. The installed app's window already names Orgus ("Orgus - Overview"), so
 * there it is the page alone; a browser tab says both.
 */
export function documentTitle(heading: string, installed: boolean): string {
  if (heading === 'Orgus') return 'Orgus';
  return installed ? heading : `${heading} · Orgus`;
}

type ThemeColorDoc = Pick<Document, 'documentElement' | 'querySelectorAll' | 'createElement' | 'head'>;

/**
 * Point every theme-color meta at the frame colour of the theme on screen. index.html picks one
 * by the computer's scheme; the person's own choice wins once the app runs.
 */
export function syncThemeColor(doc: ThemeColorDoc = document, colour = getComputedStyle(doc.documentElement).getPropertyValue('--bg-rail').trim()): void {
  if (!colour) return;
  const metas = [...doc.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')];
  if (!metas.length) {
    const meta = doc.createElement('meta');
    meta.name = 'theme-color';
    doc.head.append(meta);
    metas.push(meta);
  }
  for (const meta of metas) {
    meta.removeAttribute('media');
    meta.content = colour;
  }
}

/** Keep the theme colour on the frame colour as the theme changes. */
export function useThemeColor(): void {
  const theme = usePrefs((s) => s.theme);
  useEffect(() => syncThemeColor(), [theme]);
}
